import {
  CalendarIcon,
  Divider,
  Flex,
  ProviderConsumer as FluentUIThemeConsumer,
  Grid,
  gridHorizontalBehavior,
  SpeakerPersonIcon,
  SvgIconSizeValue,
  TeamCreateIcon,
  Text
} from '@fluentui/react-northstar';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppCSS from '../../../../../src/components/App.module.css';
import AgentTimeIcon from '../../../../svg/agent-time-filled.svg';
import AssigneeIcon from '../../../../svg/assignee.svg';
import CategoryIcon from '../../../../svg/category.svg';
import CustomPropertiesIcon from '../../../../svg/custom-properties-filled.svg';
import EmailIcon from '../../../../svg/email.svg';
import GroupIcon from '../../../../svg/group.svg';
import PriorityIcon from '../../../../svg/priority-icon.svg';
import TagsIcon from '../../../../svg/tags-icon.svg';
import TemplateIcon from '../../../../svg/template.svg';
import TicketTypeIcon from '../../../../svg/ticket-type-filled.svg';
import TicketSVG from '../../../../svg/ticket.svg';
import { appState, useSetState } from '../../../AppState';
import { CustomPropertyDataType_LABELS } from '../../../custom-properties/utils/constants';
import { formatTimeInput } from '../../../templates/CustomComponents/Utils/CustomElementUtils';
import { threedigitized } from '../../../tikit/ticketHelper';
import { ConvertDateTimeToLocale, ConvertDateToLocale } from '../../common/ConvertDate';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { ICustomProperty } from '../../interfaces/customproperties.interface';
import { LICENSE_FEATURE, TICKET_DETAIL } from '../../utils/constants';
import { toTitleCase } from '../../utils/helper';
import { findWithArchived } from '../../utils/archivedLookups';
import { CheckFeature } from '../CheckFeature';
import { SlaPills } from '../SlaPills/SlaPills';
import { TicketStatus } from '../TicketCardTemplate';
import { renderTimeInputInRequest } from '../TicketTemplateAnswer';
import { DurationText } from '../TicketTimeTracking/DurationText';
import { UserAvatar } from '../UserAvatar';
import { DisplayLifecycleWithPhases } from './displayLifecyclePhases/DisplayLifecycleWithPhases';
import Styles from './TicketDetailInfo.module.css';
import { TicketDetailCounters } from './TicketDetailCounters';
import { Title } from './Title';

const iconStyle = {
  size: 'small' as SvgIconSizeValue,
  style: { color: '#8f90a6', width: "12px", height: "12px" }
};

// The footer pairs an icon with its label. A tighter gap here than the 8px separating each
// pair from its value is what makes the icon read as belonging to the label next to it.
const footerLabelStyles = { gap: '3px' };

// Each footer fact is icon + label + value. Grouping the pair binds the value to its own
// label at 6px and lets the three facts stand apart at 16px. Flat, they all sat at 8px, so
// a value was no closer to the label it belongs to than to the next fact along.
const footerFactStyles = { gap: '6px' };

// A raw svg is a shrinkable flex item, unlike the Fluent icons beside it, so it squashes as
// the row runs out of room. Pinning it keeps the clock the same size at every window width.
const agentTimeIconStyle = { flexShrink: 0, minWidth: '15px' };

/**
 * The newer of two timestamps, tolerating either being absent or unparseable - an
 * unreadable value loses rather than poisoning the comparison with NaN.
 */
const pickLatestDate = (a?: string, b?: string): string | undefined => {
  if (!a) return b;
  if (!b) return a;
  const aTime = new Date(a).getTime();
  const bTime = new Date(b).getTime();
  if (Number.isNaN(aTime)) return b;
  if (Number.isNaN(bTime)) return a;
  return aTime >= bTime ? a : b;
};

const showRequester = requester => {
  let name = '';
  if (requester && requester?.FullName) {
    name = requester?.FullName;
  } else {
    name = 'No Requester';
  }
  return name;
};

const setIdColor = (idColorPurple: boolean, globalTheme: any): React.CSSProperties => {
  return idColorPurple ? { color: `${globalTheme.siteVariables.colorScheme.brand.foreground}`, fontWeight: 'bold' } : {};
};

interface Props {
  attachmentsCount?: number;
  commentsCount?: number;
  isBoardMobileView?: boolean;
  idColorPurple?: boolean;
  ticketData: any;
  showViewMore?: boolean;
  showDetailPopup?: (value: boolean) => void;
  style?: React.CSSProperties;
  detailCardApprovalsList?: TicketApprovals[];
  detailCardTaskList?: TicketTask[];
  customProperties?: ICustomProperty[];
  /** Tenant setting. Off hides Total Agent Time, so a tenant that has turned the feature
   *  off stops seeing historical totals rather than being reminded of them. */
  isTimeTrackingEnabled?: boolean;
  /**
   * The ticket's live Total Agent Time, which never reaches `ticketData`: writing it there
   * would change the ticket object's identity and make EditTicketForm reset itself,
   * discarding the user's unsaved property edits. Carries the modified stamp only when the
   * time was logged on this page, so the two footer values move together in that case; a
   * total picked up from another agent moves on its own.
   */
  agentTimeUpdate?: { totalMinutes: number; modifiedDate?: string };
}

export const showParentChild = (item: any, itemArray: any) => {
  let values = [];

  function dataManiplate(selectedItem) {
    if (!selectedItem?.ParentId) {
      values = [];
      if (selectedItem?.Value) {
        values.push(selectedItem?.Value);
      }
      return;
    }
    const parentCategoryValue = findWithArchived(itemArray, selectedItem.ParentId);
    dataManiplate(parentCategoryValue);
    values.push(selectedItem?.Value);
  }
  dataManiplate(item);
  return values;
};

export const getCustomPropertyDisplayData = (customPropsJson: any, cp: ICustomProperty) => {
  let data = customPropsJson[cp.PropertyId]?.toString();
  switch (CustomPropertyDataType_LABELS[cp.DataType]) {
    case CustomPropertyDataType_LABELS.DATE:
      data = (data?.length ?? 0) > 0 ? ConvertDateToLocale(customPropsJson[cp.PropertyId], DateTime.DATE_MED_WITH_WEEKDAY) : null;
      break;
    case CustomPropertyDataType_LABELS.TIME:
      data = (data?.length ?? 0) > 0 ? renderTimeInputInRequest(formatTimeInput(customPropsJson[cp.PropertyId])) : null;
      break;
    case CustomPropertyDataType_LABELS.LIST:
      const itemValue: string[] = (customPropsJson[cp.PropertyId]?.toString() ?? "").split(",").map(v => v.trim()).filter(v => v.length > 0);
      const item = cp?.CustomPropertyListItems
        ?.filter(c => itemValue.includes(c.Id.toString()))
        ?.sort((a, b) => itemValue.indexOf(a.Id.toString()) - itemValue.indexOf(b.Id.toString()))
        ?.map(m => m.Value) ?? [];
      data = item.length > 0 ? item.join(", ") : null;
      break;
    case CustomPropertyDataType_LABELS.BOOL:
      if (data)
        data = toTitleCase(customPropsJson[cp.PropertyId]?.toString() ?? "False");
      break;
    default:
      data = customPropsJson[cp.PropertyId]?.toString();
      break;
  }
  return data;
}

const ShowMore = ({ showViewMore, showDetailPopup, ticketData, customProperties }: Props) => {
  const { t } = useTranslation();
  const _tags = ticketData?.Tags || [];
  const _tagsName = _tags.map((data: any) => data.Name).join(', ');
  const customPropsStr = ticketData?.CustomPropertiesAnswerJson ?? "{}";
  const customPropsJson = JSON.parse((customPropsStr?.length ?? 0) > 0 ? customPropsStr : "{}");

  return (
    <>
      {showViewMore && (
        <Grid accessibility={gridHorizontalBehavior} columns="1">
          <div className={`block md:hidden`}>
            <span className={`${AppCSS['ViewMore']} font-semibold text-sm mb-1`} onClick={() => { showDetailPopup(true); }}>
              {t('ticket-details.ticket-details-card.ticket-details-info.show-more')}
            </span>
          </div>
        </Grid>
      )}
      {!showViewMore && (<>
        <div className='hidden md:flex'>
          <CheckFeature featureName={LICENSE_FEATURE.TicketTags}>
            <Grid accessibility={gridHorizontalBehavior} columns="1">
              <Flex vAlign="center" gap="gap.small" className={`${Styles['grid-item']}`}>
                <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol1}>
                  <TagsIcon />
                  <Title text={TICKET_DETAIL.TAGS} />
                </Flex>
                <Flex>{_tags.length > 0 ? _tagsName : t('ticket-details.ticket-details-card.ticket-details-info.none')}</Flex>
              </Flex>
            </Grid>
          </CheckFeature>
        </div>
        <CheckFeature featureName={LICENSE_FEATURE.CustomProperties}>
          {customProperties.filter(c => !c.IsArchived && !c.IsHidden).map(prop => {
            return <div className={`hidden md:flex item-center ${Styles['grid-item']}`} key={prop.PropertyId}>
              <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol0}>
                <CustomPropertiesIcon style={{ marginLeft: '-2px' }} />
                {/* <Title text={toTitleCase(prop.Name)} /> */}
                <Text size="small" style={{ color: '#8f90a6', marginRight: '4px', marginLeft: '2px' }}>{toTitleCase(prop.Name)}:</Text>
              </Flex>
              <Flex>
                {getCustomPropertyDisplayData(customPropsJson, prop) ?? t('ticket-details.ticket-details-card.ticket-details-info.none')}{' '}

              </Flex>
            </div>
          })}
        </CheckFeature>
      </>)}
    </>
  );
};

export const TicketDetailInfo = ({
  attachmentsCount,
  commentsCount,
  isBoardMobileView = false,
  idColorPurple = false,
  ticketData,
  showViewMore = false,
  showDetailPopup,
  style = {},
  detailCardApprovalsList,
  detailCardTaskList,
  isTimeTrackingEnabled = false,
  agentTimeUpdate
}: Props) => {
  const { t, i18n } = useTranslation();
  const currentstate = appState();
  const setAppState = useSetState();
  const { categories, priority, ticketTypes, customProperties } = currentstate;
  const [category, setCategory] = useState([]);
  const [showPriority, setShowPriority] = useState([]);
  const [type, setType] = useState([]);
  useEffect(() => {
    setCategory(showParentChild(ticketData?.Category, categories));
    setShowPriority(showParentChild(ticketData?.Priority, priority));
    setType(showParentChild(ticketData?.TicketType, ticketTypes));
  }, [ticketData]);

  const ticketSlas: TicketSla[] = ticketData['TicketSlas'] || [];
  // The override wins while it is set; it is cleared on the next load, at which point the
  // server's own values are authoritative again.
  const totalAgentTimeMinutes = agentTimeUpdate?.totalMinutes ?? ticketData.TotalTimeSpentMinutes;

  // The later of the two, NOT the override outright. Both are stamped client-side the
  // moment their save is acknowledged - logging time here, and onTicketUpdate/onSendComment
  // on the ticket itself - so whichever is newer is the most recent modification the page
  // knows about. Preferring the override unconditionally froze the stamp at the time entry:
  // it is only cleared by SET_DEFAULT_TICKET, i.e. a full reload, so a property save or a
  // comment posted afterwards advanced ticketData.ModifiedDate and was never shown.
  const modifiedDate = pickLatestDate(agentTimeUpdate?.modifiedDate, ticketData.ModifiedDate);
  return (
    <FluentUIThemeConsumer
      render={globalTheme => (
        <div className="min-h-32" style={{ color: 'var(--mgt-theme-default-foreground)', ...ThemeColorScheme(globalTheme.siteVariables) }}>
          <Flex
            column
            gap="gap.small"
            style={{
              backgroundColor: 'var(--mgt-theme-background)',
              padding: '0.75rem',
              boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.07), 0px 1.6px 3.6px rgba(0, 0, 0, 0.11)',
              ...style
            }}
          >
            <div className={`md:flex items-center leading-none flex-wrap`}>
              <div className="flex items-center justify-between">
                <Flex vAlign="center" className='mr-2'>
                  <TicketSVG width={24} className={`mr-3`} />
                  <Text style={setIdColor(idColorPurple, globalTheme)}>
                    #{threedigitized(ticketData?.Id)}
                  </Text>
                </Flex>

                <div className='md:hidden'>
                  <TicketStatus status={ticketData?.Status} />
                </div>
              </div>
              {/* tailwind `h-6` is not working because there's a style overriding it */}
              <div className='mr-2 hidden md:block' >
                <Divider vertical style={{ height: "24px" }} />
              </div>
              <div className='flex items-center mt-2 md:mt-0'>
                {ticketData?.Requester != null ? (
                  <UserAvatar
                    {...{
                      idOrUpn: ticketData.Requester?.AadObjectId ?? ticketData.Requester?.UserName,
                      avatarProps: { name: ticketData.Requester?.FullName, size: 'small' }
                    }}
                  />
                ) : (
                  <SpeakerPersonIcon
                    size="small"
                    style={{ color: '#8f90a6' }}
                    variables={{ largeSize: '30px' }}
                  />
                )}

                <Text weight="semibold" size="medium" className='ml-2 truncate'>
                  {showRequester(ticketData?.Requester)}
                </Text>
              </div>
              <Flex vAlign="center" gap="gap.smaller" className='mt-2 md:mt-0 md:ml-2 flex-grow text-xs'>
                <span>{t('ticket-details.ticket-details-card.ticket-details-info.created')} {ConvertDateTimeToLocale(ticketData['CreatedDate'], DateTime.DATE_MED_WITH_WEEKDAY)}</span>
                {ticketData?.Source?.Value == 'Email' ? (
                  <>
                    <span className='text-xs'>{t('ticket-details.ticket-details-card.ticket-details-info.via-email')}</span>
                    <span>
                      <EmailIcon width={12} height={12} stroke="#8f90a6" />{' '}
                    </span>
                  </>
                ) : ('')}
              </Flex>
              <div className='flex items-center mt-2 md:mt-0'>
                <Flex vAlign="center" gap="gap.small">
                  <Flex vAlign="center" gap="gap.smaller">
                    <PriorityIcon width={20} height={20} className={Styles.priorityDot} />
                  </Flex>
                  <div className={`w-full truncate text-xs`} dir="rtl">
                    <bdi>{showPriority.length > 0 ? showPriority.join(' > ') : t('ticket-details.ticket-details-card.ticket-details-info.none')}{' '}</bdi>
                  </div>
                </Flex>
                <div className='mx-2 hidden md:block' >
                  <Divider vertical style={{ height: "24px" }} />
                </div>
                <div className='hidden md:block'>
                  <TicketStatus status={ticketData?.Status} />
                </div>
              </div>
            </div>

            <Text weight="semibold" className="break-all">{ticketData.Title}</Text>

            <div className={`w-full sm:text-sm hidden md:block`}>
              <div className={`grid grid-cols-1 md:grid-cols-3`}>
                {!showViewMore && (
                  <>
                    <Flex vAlign="center" gap="gap.small" className={`${Styles['grid-item']}`}>
                      <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol0}>
                        <AssigneeIcon width={12} height={13} />
                        <Title text={toTitleCase(TICKET_DETAIL.ASSIGNEE)} />
                      </Flex>
                      <Flex>{ticketData?.Assignee?.FullName ?? t('ticket-details.ticket-details-card.ticket-details-info.none')}</Flex>
                    </Flex>
                    <Flex vAlign="center" gap="gap.small" className={`${Styles['grid-item']}`}>
                      <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol1}>
                        <GroupIcon />
                        <Title text={toTitleCase(TICKET_DETAIL.GROUP)} />
                      </Flex>
                      <Flex>
                        {ticketData?.SupportGroup ? ticketData.SupportGroup['Name'] : t('ticket-details.ticket-details-card.ticket-details-info.none')}{' '}
                      </Flex>
                    </Flex>
                    <Flex vAlign="center" gap="gap.small" className={`${Styles['grid-item']}`}>
                      <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol2}>
                        <CategoryIcon width={12} height={12} stroke="#8f90a6" />
                        <Title text={toTitleCase(TICKET_DETAIL.CATEGORY)} />
                      </Flex>
                      <div className={`w-full truncate mr-4`} dir="rtl">
                        <bdi>
                          {category.length > 0 ? category.join(' > ') : t('ticket-details.ticket-details-card.ticket-details-info.none')}{' '}
                        </bdi>
                      </div>
                    </Flex>
                    <CheckFeature featureName={LICENSE_FEATURE.MultiDepartment}>
                      <div className={`hidden md:flex item-center ${Styles['grid-item']}`} >
                        <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol0}>
                          <TeamCreateIcon {...iconStyle} />
                          <Title text={toTitleCase(TICKET_DETAIL.TEAM)} />
                        </Flex>
                        <Flex>
                          {ticketData?.Team ? ticketData.Team['FriendlyName'] : t('ticket-details.ticket-details-card.ticket-details-info.none')}{' '}
                        </Flex>
                      </div>
                    </CheckFeature>
                    <CheckFeature featureName={LICENSE_FEATURE.TicketTypes}>
                      <div className={`hidden md:flex item-center ${Styles['grid-item']}`} >
                        <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol1}>
                          <TicketTypeIcon
                            style={{ marginRight: '5px', fill: '#8f90a6' }}
                            width={16}
                            height={16}
                            stroke="#8f90a6"
                          />
                          <Title text={toTitleCase(TICKET_DETAIL.TICKET_TYPE)} />
                        </Flex>
                        <div className={`w-full truncate mr-4`} dir="rtl">
                          <bdi>{type.length > 0 ? type.join(' > ') : t('ticket-details.ticket-details-card.ticket-details-info.none')}{' '}</bdi>
                        </div>
                      </div>
                    </CheckFeature>
                    <div className={`hidden md:flex item-center ${Styles['grid-item']}`} >
                      <Flex vAlign="center" gap="gap.smaller" className={Styles.fieldDetailsCol2}>
                        <TemplateIcon />
                        <Title text={toTitleCase(TICKET_DETAIL.TEMPLATE)} />
                      </Flex>
                      <div className={`w-full truncate mr-4`} title={ticketData?.Template?.Name ? ticketData?.Template?.Name : t('ticket-details.ticket-details-card.ticket-details-info.none')}>
                        <bdi>{ticketData?.Template?.Name ? ticketData?.Template?.Name : t('ticket-details.ticket-details-card.ticket-details-info.none')} </bdi>
                      </div>
                    </div>
                    <CheckFeature featureName={LICENSE_FEATURE.ServiceLevelAgreements}>
                      <Flex vAlign="center" gap="gap.small" className={`${Styles.fieldDetailsCol0} ${Styles['grid-item']}`}>
                        <SlaPills ticketSlas={ticketSlas} svgGap={false} textSize="medium" />
                      </Flex>
                    </CheckFeature>
                    <ShowMore
                      showViewMore={showViewMore}
                      ticketData={ticketData}
                      showDetailPopup={showDetailPopup}
                      customProperties={customProperties.sort((a, b) => a.Order - b.Order)}
                    />
                  </>
                )}
              </div>
            </div>
            <div className="hidden md:block">
              <DisplayLifecycleWithPhases ticketData={ticketData} isAnalyst />
            </div>
            <div style={{ borderTop: '1px solid lightgrey' }} className="hidden md:flex flex-col sm:flex-row items-start justify-between gap-1 pt-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center" style={footerFactStyles}>
                  <Flex vAlign="center" styles={footerLabelStyles}>
                    <CalendarIcon style={{ color: '#8f90a6' }} />
                    <Title text={toTitleCase(TICKET_DETAIL.MODIFIED)} />
                  </Flex>
                  <Flex>
                    <span className="text-xs">{ConvertDateTimeToLocale(modifiedDate, DateTime.DATE_MED_WITH_WEEKDAY)}</span>
                  </Flex>
                </div>
                {ticketData?.DueDate && (
                  <div className="flex items-center" style={footerFactStyles}>
                    <Flex vAlign="center" styles={footerLabelStyles}>
                      <CalendarIcon style={{ color: '#8f90a6' }} />
                      <Title text={toTitleCase(TICKET_DETAIL.DUE_DATE)} />
                    </Flex>
                    <Flex>
                      <span className="text-xs">{ConvertDateToLocale(ticketData.DueDate, DateTime.DATE_MED_WITH_WEEKDAY)}</span>
                    </Flex>
                  </div>
                )}
                {/* Sits with the other time facts, below the divider, because it is derived
                    rather than an editable ticket property. Shown whenever the tenant has
                    the feature on, including at zero - "0m" states that nothing has been
                    recorded, where an absent row reads as "not tracked here". Matches the
                    Teams card and the view column. */}
                {isTimeTrackingEnabled && (
                  <div className="flex items-center" style={footerFactStyles}>
                    <Flex vAlign="center" styles={footerLabelStyles}>
                      <AgentTimeIcon width={15} height={15} style={agentTimeIconStyle} />
                      <Title text={toTitleCase(TICKET_DETAIL.TOTAL_AGENT_TIME)} />
                    </Flex>
                    <Flex>
                      <span className="text-xs"><DurationText minutes={totalAgentTimeMinutes} plain /></span>
                    </Flex>
                  </div>
                )}
              </div>
              {!isBoardMobileView &&
                <div className="flex items-center">
                  <TicketDetailCounters
                    ticketData={ticketData}
                    globalTheme={globalTheme}
                    attachmentsCount={attachmentsCount}
                    commentsCount={commentsCount}
                    detailCardApprovalsList={detailCardApprovalsList}
                    detailCardTaskList={detailCardTaskList}
                  />
                </div>}
            </div>
          </Flex>
        </div>
      )}
    />
  );
};
